import { tt } from '@agos/shared'

/**
 * Who may write which half of `/loyalty-program`.
 *
 * One page, **two** write rules, because it drives two controllers:
 *
 * | Tab | Endpoints | Read | Write |
 * |---|---|---|---|
 * | Nastavenia | `PUT /settings/loyalty`, `POST`/`PATCH`/`DELETE /loyalty/levels…` | see below | **`main_admin`** |
 * | Kampane | `PATCH /campaigns/{type}`, `POST /campaigns/{type}/run`, `PATCH /campaigns/send-cap` | `main_admin`, `admin` | `main_admin`, `admin` |
 *
 * The route itself is gated on the **permission** `loyalty_program`, and in this data that permission is
 * granted to `main_admin` and nobody else — so today only a main admin arrives here and the distinction is
 * invisible. It is not invisible for long: `role_permissions` is edited at runtime from `/permissions`, and
 * the moment `loyalty_program` reaches `admin`, an admin who may run every campaign on the second tab may
 * store nothing at all on the first. Guessing one rule for the page would then be wrong on one tab whichever
 * rule was guessed.
 *
 * This is FB-001's resolution applied to a page rather than to a module: admit whoever the server lets read,
 * disable exactly what it will not let them store, and say which.
 *
 * `GET /api/v1/settings/loyalty` is `@RequireAuth({"main_admin", "admin"})` — measured 2026-08-11, a
 * request with no token answers **401**. It carried no guard at all when this note was first written
 * (FB-138), which is the sentence that used to be here; the customer app reads `/loyalty/public-info`
 * because that endpoint carries the Resend key and every mail and SMS template, not because it is open.
 */

export type LoyaltyTab = 'settings' | 'campaigns'

export function mayWrite(tab: LoyaltyTab, role: string | undefined): boolean {
  if (role === 'main_admin') return true
  // Every campaigns write is `@RequireAuth({"main_admin", "admin"})`; every settings write is main-admin-only.
  return tab === 'campaigns' && role === 'admin'
}

/** Said on the tab, before anything is typed into a field that cannot be stored. */
export function readOnlyReason(tab: LoyaltyTab, role: string | undefined): string | null {
  if (mayWrite(tab, role)) return null
  if (role === 'admin') {
    return tt(
      'Vernostný program môže meniť len hlavný administrátor. Vidieť ho môžete, uložiť nie - server zmenu odmietne. Kampane na druhej záložke meniť môžete.',
    )
  }
  return tt('Na zmenu vernostného programu nemáte právo. Údaje vidíte, uložiť ich nemôžete.')
}
